import { useEffect, useState, useMemo, type ComponentProps } from "react";
import { TextInput, useDataProvider, useRecordContext } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import debounce from "lodash.debounce";
import { formFieldsFromItsDirectoryHit, shouldLookupItsDirectory } from "./its-directory-lookup";

type ManualITSInputProps = ComponentProps<typeof TextInput>;

export const ManualITSInput = (props: ManualITSInputProps) => {
  const { setValue } = useFormContext();
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  useWatch({ name: "sabilType" });
  const itsNo = useWatch({ name: "itsNo" });

  const lookupItsData = useMemo(
    () =>
      debounce(async (itsNoValue: string) => {
        if (!itsNoValue.trim()) {
          return;
        }

        setLoading(true);
        try {
          const { data } = await dataProvider.getList("itsData", {
            filter: { ITS_ID: itsNoValue.trim() },
            pagination: { page: 1, perPage: 1 },
            sort: { field: "id", order: "ASC" },
          });

          const fields = formFieldsFromItsDirectoryHit(
            data?.[0] as { Full_Name?: string; Address?: string } | undefined
          );
          if (!fields) {
            return;
          }
          setValue("name", fields.name);
          setValue("address", fields.address);
        } catch (error) {
          console.error("Error looking up ITS data:", error);
        } finally {
          setLoading(false);
        }
      }, 500),
    [dataProvider, setValue]
  );

  useEffect(() => {
    if (shouldLookupItsDirectory(itsNo, record?.itsNo)) {
      lookupItsData(itsNo as string);
    }
    return () => {
      lookupItsData.cancel();
    };
  }, [itsNo, lookupItsData, record?.itsNo]);

  return <TextInput {...props} disabled={loading} />;
};
